import React from "react";
import { Box, Grid } from "@mui/material";
import DirectionsCar from "@mui/icons-material/DirectionsCar";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import {
  FleetLogisticsReport,
  WarehouseInventoryReport,
  ifasReportsService,
  IFASReportFilter,
} from "../../../../../services/ifasReportsService";
import { IFASReportTable, ColumnDef } from "../components/IFASReportTable";
import { ReportKpiCard, ReportStatusChip, toneFromMuiColor } from "../components/ReportPrimitives";
import { formatR } from "../../../../../utils/money";

interface Props {
  fleet: FleetLogisticsReport[];
  inventory: WarehouseInventoryReport[];
  loading: boolean;
  filter: IFASReportFilter;
  showFleet: boolean;
  showInventory: boolean;
}

export const FleetWarehouseReportsTab: React.FC<Props> = ({ fleet, inventory, loading, filter, showFleet, showInventory }) => {
  const lowStockItems = inventory.filter(
    (i) => i.status?.toLowerCase() === "low_stock" || i.status?.toLowerCase() === "out_of_stock"
  ).length;
  const totalStockItems = inventory.length;

  const fleetColumns: ColumnDef<FleetLogisticsReport>[] = [
    { id: "funeralNumber", label: "Funeral No", minWidth: 130 },
    { id: "registrationNumber", label: "Registration No", minWidth: 140 },
    { id: "vehicleType", label: "Vehicle Type", minWidth: 160 },
    { id: "driverName", label: "Driver", minWidth: 150 },
    { id: "allocationDate", label: "Allocation Date", minWidth: 130 },
    {
      id: "status",
      label: "Status",
      minWidth: 120,
      format: (val) => <ReportStatusChip label={val} tone={toneFromMuiColor("primary")} />,
    },
  ];

  const inventoryColumns: ColumnDef<WarehouseInventoryReport>[] = [
    { id: "itemCode", label: "Item Code", minWidth: 130 },
    { id: "itemName", label: "Item Name", minWidth: 180 },
    { id: "category", label: "Category", minWidth: 140 },
    { id: "currentStock", label: "Current Stock", minWidth: 110, align: "right" },
    { id: "minThreshold", label: "Min Threshold", minWidth: 110, align: "right" },
    {
      id: "unitPrice",
      label: "Unit Price",
      minWidth: 120,
      align: "right",
      format: (val) => formatR(val as number | string | null | undefined),
    },
    {
      id: "status",
      label: "Stock Status",
      minWidth: 120,
      format: (val) => {
        const color = val === "NORMAL" ? "success" : val === "LOW_STOCK" ? "warning" : "error";
        return <ReportStatusChip label={val} tone={toneFromMuiColor(color)} />;
      },
    },
  ];

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {showFleet && <Grid size={{ xs: 12, sm: showInventory ? 4 : 12 }}>
          <ReportKpiCard
            label="Active Fleet Allocations"
            value={fleet.length}
            caption="Vehicles on assignment"
            tone="ink"
            icon={<DirectionsCar />}
          />
        </Grid>}
        {showInventory && <Grid size={{ xs: 12, sm: showFleet ? 4 : 6 }}>
          <ReportKpiCard
            label="Total Material Lines"
            value={totalStockItems}
            caption="Tracked SKUs"
            tone="blue"
            icon={<Inventory2Outlined />}
          />
        </Grid>}
        {showInventory && <Grid size={{ xs: 12, sm: showFleet ? 4 : 6 }}>
          <ReportKpiCard
            label="Low Stock / Out of Stock"
            value={`${lowStockItems} Items`}
            caption="Need replenishment"
            tone="red"
            icon={<WarningAmberOutlined />}
          />
        </Grid>}
      </Grid>

      {showFleet && <IFASReportTable
        title="Fleet, Transport & Logistics Report"
        subtitle="Courtesy vehicles, buses, drivers, transport allocations & body removals summary"
        columns={fleetColumns}
        data={fleet}
        loading={loading}
        onExport={(fmt) => ifasReportsService.downloadReportExport("fleet-logistics", fmt, filter)}
      />}

      {showInventory && <IFASReportTable
        title="Warehouse & Inventory Management Report"
        subtitle="Daily Picking List, Stock Take, Dispatches, Low Stock Alerts & Stock Movement Audit Trail"
        columns={inventoryColumns}
        data={inventory}
        loading={loading}
        onExport={(fmt) => ifasReportsService.downloadReportExport("warehouse-inventory", fmt, filter)}
      />}
    </Box>
  );
};
